/**
 * organView.js — «ТОЛЬКО ОРГАН В 3D»: мост от маски сегментации к объёмному
 * виду. [11.1.24, по слову владельца «проектируй»]
 *
 * ЗАЧЕМ. Пресет — фильтр по яркости: «покажи всё 25…130 HU» — и на нативе
 * почка тонет в мышцах той же плотности (предел метода, названный в титлах
 * 11.1.23). Сегментация — трафарет ФОРМЫ: воксели, помеченные врачом кистью
 * или пороговой кистью, — и трафарету плотность безразлична. Этот модуль
 * даёт маске третье измерение: тело гаснет, орган остаётся объёмно, рядом —
 * его объём в миллилитрах и STL в миллиметрах.
 *
 * КАК. Объёмное окно штатно умеет менять том (setVolumesForViewports — тем
 * же путём ходит восстановление после сна, viewport.js:1394). Маска УЖЕ
 * лежит в кэше томов Cornerstone (её оттуда же читает экспорт NIfTI):
 * подменяем том окна на маску, ставим передаточные функции «ступенькой»
 * вокруг номера сегмента — виден ровно он. «Вернуть тело» подменяет том
 * обратно; последний VRT-пресет пере-применяет вызывающий (featureBindings),
 * у которого есть доступ к памяти sidebarBindings.
 *
 * СТРОЕНИЕ. Чистая математика (подсчёт, объём, бинаризация) отделена от
 * cornerstone-клея и проверяется verify функционально на игрушечном массиве.
 */

import { getActiveSegmentationId, getSegmentationData } from '../tools/toolManager.js';
import { getRenderingEngine, cornerstone as _cs } from './viewportState.js';
import { cornerstoneTools as _csTools } from './viewportState.js';   // [11.1.38] событие «маска изменилась»
import { log } from '../utils/logger.js';

// [11.1.24] Чистая математика вынесена в organMath.js (ноль импортов):
// этот файл тянет toolManager, которому нужен window, — verify в node его
// не откроет, а числа проверять обязан. Реэкспорт сохраняет старые адреса.
import { подсчётВокселей, объёмМл, бинаризоватьСегмент, цветВRgb } from './organMath.js';
import { вырезатьПоРамке } from './maskMorph.js';   // [11.1.41] STL по вырезке, а не по всему тому
import { planPresetApply } from './presetPlan.js';   // [12.0.27] органы — в то же окно, что пресеты
export { подсчётВокселей, объёмМл, бинаризоватьСегмент, цветВRgb };

// ══════════════════════════════════════════════════════════════
//  CORNERSTONE-КЛЕЙ
// ══════════════════════════════════════════════════════════════

/** Том маски активной сегментации — тем же путём, что экспорт NIfTI. */
export function томМаски() {
  const segId = getActiveSegmentationId();
  if (!segId) return { ошибка: 'Сегментации ещё нет: возьмите кисть и проведите по снимку' };
  const данные = getSegmentationData(segId);
  const L = данные?.representationData?.Labelmap;
  // [11.1.42] ПОЧЕМУ «МАСКА НЕ НАЙДЕНА В ПАМЯТИ» (жалоба владельца: не
  // работали ни «Экспорт NIfTI», ни «STL органа», ни рамка роста области).
  // Мы читали ТОЛЬКО L.volumeId. В Cornerstone 5 у labelmap появился слой
  // «labelmaps»: том может лежать в L.labelmaps[primaryLabelmapId].volumeId,
  // а верхнее поле остаться пустым (сверено с types/LabelmapTypes.ts —
  // LabelmapSegmentationDataShared.labelmaps). Тогда всё, что ходит через
  // томМаски, честно говорило «не найдена». Теперь перебираем все известные
  // места, а напоследок ищем в кеше по имени сегментации.
  const кэш = () => window.cornerstone?.cache || _cs?.cache;
  const взять = (id) => { try { return id ? кэш()?.getVolume?.(id) : null; } catch (e) { return null; } };
  const кандидаты = [L?.volumeId];
  try {
    const слои = L?.labelmaps || {};
    const главный = L?.primaryLabelmapId;
    if (главный && слои[главный]?.volumeId) кандидаты.push(слои[главный].volumeId);
    for (const к of Object.keys(слои)) if (слои[к]?.volumeId) кандидаты.push(слои[к].volumeId);
  } catch (e) { /* структура иная — пойдём поиском по кешу */ }
  let volumeId = null; let том = null;
  for (const к of кандидаты) { const т = взять(к); if (т) { volumeId = к; том = т; break; } }
  if (!том) {
    // Последний рубеж: том маски заводится нами с именем «<segId>-labelmap».
    //
    // [11.1.71] УСЛОВИЕ СУЖЕНО. Здесь стояло
    //     if (id.includes(segId) || id.includes('labelmap'))
    // и вторая половина брала ЛЮБОЙ labelmap-том из кеша — в том числе
    // оставшийся от прежней серии.
    //
    // Живой случай владельца: вчера он размечал печень на серии 818 срезов,
    // сегодня открыл серию 808 и импортировал маску от TotalSegmentator.
    // Сегментации в этой сессии ещё НЕ БЫЛО, а отказ говорил: «в файле
    // 512×512×808, в открытом исследовании 512×512×818». Откуда 818? Отсюда:
    // томМаски цеплял вчерашний labelmap по слову в имени, и сверка сравнивала
    // файл с чужим томом. Врач при этом видел «стирать нечего, сегментации
    // ещё нет» — и был прав, а программа противоречила сама себе.
    //
    // Теперь берётся ТОЛЬКО том активной сегментации: её идентификатор входит
    // в имя тома по построению (#tryCreateSegmentation зовёт
    // createAndCacheDerivedLabelmapVolume с именем «<segId>-labelmap»).
    // Совпадение по слову «labelmap» — не признак принадлежности, а совпадение
    // букв.
    try {
      for (const v of (кэш()?.getVolumes?.() || [])) {
        const id = String(v?.volumeId || '');
        if (id.includes(segId)) { volumeId = id; том = v; break; }
      }
    } catch (e) { /* кеш недоступен */ }
  }
  if (!том) {
    log.warn(`[Маска] том не найден. segId=${segId}, кандидаты=[${кандидаты.filter(Boolean).join(', ') || 'пусто'}]`);
    return { ошибка: 'Маска не найдена в памяти — сегментация ещё строится?' };
  }
  const пиксели = том.voxelManager?.getCompleteScalarDataArray?.() || том.getScalarData?.();
  if (!пиксели?.length) return { ошибка: 'Маска пуста: рисовать ещё нечего' };
  return { том, volumeId, пиксели, dims: том.dimensions, spacing: том.spacing || [1, 1, 1] };
}

/**
 * [11.1.38] ЗАПИСЬ В МАСКУ + ОБНОВЛЕНИЕ ПОКАЗА. Морфология (заливка контуров,
 * интерполяция между срезами) считает на КОПИИ массива — getCompleteScalarData
 * Array в Cornerstone 5 возвращает копию, и правка «на месте» без этой
 * функции ушла бы в никуда. Здесь копия кладётся обратно и объявляется
 * изменённой, иначе экран показывает старую маску.
 * @returns {{ок:boolean, причина?:string}}
 */
export function записатьМаску(пиксели) {
  try {
    const segId = getActiveSegmentationId();
    if (!segId) return { ок: false, причина: 'Сегментации нет' };
    // [11.1.42] Тот же поиск тома, что в томМаски — двух дорог к одному
    // понятию не заводим (доктрина «один хозяин»).
    const найдено = томМаски();
    if (найдено.ошибка) return { ок: false, причина: найдено.ошибка };
    const том = найдено.том;
    if (typeof том.voxelManager?.setCompleteScalarDataArray === 'function') {
      том.voxelManager.setCompleteScalarDataArray(пиксели);
    } else if (том.getScalarData) {
      const свой = том.getScalarData();
      if (свой && свой !== пиксели) свой.set(пиксели);
    } else {
      return { ок: false, причина: 'Эта сборка не даёт записать маску' };
    }
    try { том.imageData?.modified?.(); } catch (e) { /* показ обновится событием ниже */ }
    try {
      const сег = _csTools?.segmentation;
      (сег?.triggerSegmentationEvents?.triggerSegmentationDataModified
        || сег?.triggerSegmentationDataModified)?.(segId);
    } catch (e) { /* событие необязательно — картинку добьёт рендер */ }
    try {
      const re = getRenderingEngine();
      re?.renderViewports?.(re.getViewports().map((v) => v.id));
    } catch (e) { /* */ }
    return { ок: true };
  } catch (e) {
    return { ок: false, причина: 'Запись маски: ' + e.message };
  }
}

// ══════════════════════════════════════════════════════════════
//  ОТМЕНА ПРАВОК МАСКИ — МНОГОСТУПЕНЧАТАЯ. [11.1.86]
//
//  Повод 11.1.40 — живой случай владельца: рост области с широким диапазоном
//  протёк по всему животу (5 млн вокселей) и МОЛЧА влился в сегмент, где лежала
//  аккуратная ручная работа на 617 мл. Тогда я сделал ОДНУ ступень.
//
//  Повод 11.1.86 — его же слова: «у меня из набора есть заливка, достройка, рост
//  области и отмена одной ступени — поэтому утёкший рост приходится откатывать
//  целиком, а не подчищать». Одной ступени мало ровно потому, что доводка — это
//  ЦЕПОЧКА мелких шагов: сгладил, убрал острова, залил дыры, притянул границу. С
//  одной ступенью врач боится делать второй шаг, потому что первый станет
//  неотменяемым. Это не неудобство, это запрет на работу.
//
//  ХРАНИМ ДЕЛЬТАМИ, А НЕ КОПИЯМИ. Том 512×512×818 весит 214 МБ; двадцать
//  снимков — четыре гигабайта и мёртвая вкладка. Дельта — список изменённых
//  индексов (по 4 байта) плюс прежние значения (по 1 байту): 5 байт на
//  изменённый воксель. Утёкший рост на 5 млн вокселей — 25 МБ, аккуратная
//  правка — единицы килобайт.
//
//  ПОТОЛОК ПАМЯТИ НАЗВАН ЧИСЛОМ И СОБЛЮДАЕТСЯ. Когда стопка перерастает
//  ПОТОЛОК_МБ, самые старые ступени выбрасываются — молча забыть их было бы
//  обманом, поэтому естьПравкаМаски() отдаёт число ступеней, и панель его
//  показывает. Врач видит «отмен: 7», а не гадает.
// ══════════════════════════════════════════════════════════════

const СТУПЕНЕЙ_МАКС = 20;      // больше и не нужно: доводка — это единицы шагов
const ПОТОЛОК_МБ = 192;        // при 5 байтах на воксель это около 40 млн правок

let _стопка = [];              // [{ индексы: Int32Array, прежние: Uint8Array, что }]

/** Сколько памяти занимает стопка правок. */
function _весСтопкиМБ() {
  let байт = 0;
  for (const ш of _стопка) байт += ш.индексы.byteLength + ш.прежние.byteLength;
  return байт / 1048576;
}

/**
 * Запомнить правку маски: индексы изменённых вокселей и их прежние значения.
 * Кладётся НОВОЙ ступенью поверх прежних — они не затираются.
 */
export function запомнитьПравкуМаски(индексы, пиксели, что = 'правка', прежниеГотовые = null) {
  try {
    if (!индексы?.length) return false;
    const и = Int32Array.from(индексы);
    const п = new Uint8Array(и.length);
    // [11.1.40] Прежние значения либо приходят готовыми (морфология собирает
    // их ВНУТРИ операции — после неё массив уже изменён, снять поздно), либо
    // снимаются здесь, если вызвано ДО изменения (так делает рост области).
    if (прежниеГотовые?.length === и.length) {
      for (let k = 0; k < и.length; k++) п[k] = прежниеГотовые[k];
    } else {
      if (!пиксели?.length) return false;
      for (let k = 0; k < и.length; k++) п[k] = пиксели[и[k]];
    }
    _стопка.push({ индексы: и, прежние: п, что: String(что) });
    // Потолок по числу ступеней и по памяти. Выбрасываем САМЫЕ СТАРЫЕ: свежие
    // правки отменяют чаще, и терять их было бы обиднее.
    while (_стопка.length > СТУПЕНЕЙ_МАКС) _стопка.shift();
    while (_стопка.length > 1 && _весСтопкиМБ() > ПОТОЛОК_МБ) _стопка.shift();
    return true;
  } catch (e) { return false; }
}

/**
 * Есть ли что отменять и что именно — для подписи кнопки.
 * @returns {{что:string, вокселей:number, ступеней:number, мб:number}|null}
 */
export function естьПравкаМаски() {
  if (!_стопка.length) return null;
  const в = _стопка[_стопка.length - 1];
  return {
    что: в.что, вокселей: в.индексы.length,
    ступеней: _стопка.length, мб: Math.round(_весСтопкиМБ() * 10) / 10,
  };
}

/** Список ступеней сверху вниз — чтобы врач видел, что именно откатится. */
export function ступениПравок() {
  return _стопка.map((ш) => ({ что: ш.что, вокселей: ш.индексы.length })).reverse();
}

/**
 * Забыть все ступени. Зовётся, когда прежние правки заведомо неприменимы:
 * маску стёрли целиком или подменили импортом. Отмена, которая накладывает
 * старую дельту на новую маску, испортила бы её молча.
 */
export function забытьПравкиМаски() { const n = _стопка.length; _стопка = []; return n; }

/**
 * Отменить ПОСЛЕДНЮЮ правку маски — одну ступень. Прежние значения записаны ДО
 * правки, поэтому отмена возвращает ровно то, что было, включая ручные мазки:
 * она не «стирает сегмент», а откатывает одну операцию.
 */
export function отменитьПравкуМаски() {
  if (!_стопка.length) return { ок: false, причина: 'Отменять нечего: правок маски не запомнено' };
  const м = томМаски();
  if (м.ошибка) return { ок: false, причина: м.ошибка };
  const в = _стопка[_стопка.length - 1];
  // Сверка размера: если маска сменилась (другая серия, импорт), старая дельта
  // легла бы наугад. Тогда честно забываем всю стопку.
  let макс = 0;
  for (let k = 0; k < в.индексы.length; k++) if (в.индексы[k] > макс) макс = в.индексы[k];
  if (м.пиксели.length <= макс) {
    const было = забытьПравкиМаски();
    return { ок: false, причина: `Маска сменилась — отменять уже нечего (забыто ступеней: ${было})` };
  }
  for (let k = 0; k < в.индексы.length; k++) м.пиксели[в.индексы[k]] = в.прежние[k];
  const зап = записатьМаску(м.пиксели);
  if (!зап.ок) return зап;
  _стопка.pop();
  return { ок: true, что: в.что, вокселей: в.индексы.length, осталось: _стопка.length };
}

/**
 * [12.0.27] ОКНО ДЛЯ ОРГАНОВ — ТО ЖЕ, КУДА ИДУТ ПРЕСЕТЫ.
 *
 * Здесь бралось ПЕРВОЕ объёмное окно раскладки. В MPR6 их два: volume3d
 * («как есть») и view-3d («3D · Пресет»), и первым шло volume3d. Пресеты же
 * адресуются в view-3d (presetPlan.planPresetApply). Итог — жалоба владельца:
 * «делает вид, что размечает, показала объёмы на плашке, а органы не
 * показала». Разметка проходила (печень 1653 мл, поджелудочная 134 мл — в его
 * журнале), органы ложились в volume3d, а он смотрел в развёрнутое
 * «3D · Пресет», где ничего не менялось.
 *
 * Правило адресата одно на пресеты и органы — planPresetApply, второй копии
 * не заводим: разошлись бы снова.
 */
function _окно3D() {
  const re = getRenderingEngine();
  if (!re) return null;
  const тип3д = _cs?.Enums?.ViewportType?.VOLUME_3D || 'volume3d';
  const объёмные = re.getViewports().filter((v) => v.type === тип3д || v.type === 'volume3d');
  if (!объёмные.length) return null;
  const план = planPresetApply({ viewports: объёмные.map((v) => ({ id: v.id, is3D: true })), presetName: 'органы' });
  return объёмные.find((v) => v.id === план.target) || объёмные[0];
}

/**
 * [12.0.30] То же окно наружу: поверхности органов (organSurface3D.js)
 * кладутся туда же, куда идут пресеты, — правило адресата одно.
 */
export function окноДляОрганов() { return _окно3D(); }

/**
 * [12.0.27] Дотолкнуть текстуру тома маски до видеокарты.
 *
 * Маска пишется целиком (записатьМаску → setCompleteScalarDataArray) и
 * объявляется событием «данные сегментации изменились» — этого хватает
 * срезовым окнам. Объёмному окну том маски отдаётся как обычный том, и если
 * его текстура была создана раньше записи, она может остаться старой
 * (пустой). Поэтому после подмены тома помечаем изменёнными данные и все
 * кадры текстуры — теми методами, какие есть в этой сборке Cornerstone.
 * Что сработало — пишем в журнал: без живого окна проверить это здесь нечем.
 *
 * @returns {string} какими способами текстура помечена
 */
function _освежитьТекстуру(volumeId) {
  const способы = [];
  try {
    const том = (window.cornerstone?.cache || _cs?.cache)?.getVolume?.(volumeId);
    if (!том) return 'том не найден в кеше';
    try { if (том.imageData?.modified) { том.imageData.modified(); способы.push('imageData.modified'); } } catch (e) { /* нет */ }
    const тек = том.vtkOpenGLTexture;
    const кадров = Number(том.dimensions?.[2]) || 0;
    if (тек?.setUpdatedFrame && кадров > 0) {
      for (let i = 0; i < кадров; i++) тек.setUpdatedFrame(i);
      способы.push(`setUpdatedFrame×${кадров}`);
    }
    try { if (тек?.modified) { тек.modified(); способы.push('texture.modified'); } } catch (e) { /* нет */ }
  } catch (e) {
    return 'ошибка: ' + e.message;
  }
  return способы.length ? способы.join(', ') : 'способов обновить в этой сборке нет';
}

let _прежнее = null;   // { vpId, volumeId } — что стояло в окне до изоляции

/**
 * Показать в 3D-окне ТОЛЬКО сегмент №индекс: том окна подменяется маской,
 * прозрачность — ступенькой вокруг номера, цвет — цветом строки сегмента.
 * @returns {Promise<{ок:boolean, причина?:string, мл?:number, вокселей?:number}>}
 */
/**
 * [12.0.18] НЕСКОЛЬКО ОРГАНОВ СРАЗУ, КАЖДЫЙ СВОИМ ЦВЕТОМ.
 *
 * Прежде показывался ровно один сегмент. Для «почки + селезёнка» и
 * «поджелудочная + селезёнка + почки» этого мало: пресет по плотности развести
 * их не может (селезёнка 40–55 HU, поджелудочная 30–50, мышца 40–55 — окна
 * перекрываются полностью), а маске плотность безразлична, и каждый орган
 * можно покрасить отдельно.
 *
 * Ступенька непрозрачности строится вокруг КАЖДОГО номера сегмента, между
 * ними — ноль. Поэтому соседние сегменты, не попавшие в список, остаются
 * невидимыми, а выключение одного органа не трогает остальные.
 *
 * @param {object} о
 * @param {Array<{индекс:number, цвет:string}>} о.сегменты — что показать
 * @returns {Promise<{ок:boolean, причина?:string, органы?:Array, мл?:number}>}
 */
export async function изолироватьОрганы({ сегменты = [] } = {}) {
  const список = (Array.isArray(сегменты) ? сегменты : [])
    .filter((с) => Number.isFinite(с?.индекс) && с.индекс > 0)
    .sort((а, б) => а.индекс - б.индекс);
  if (!список.length) return { ок: false, причина: 'не задано ни одного сегмента' };

  const м = томМаски();
  if (м.ошибка) return { ок: false, причина: м.ошибка };

  // Пустые сегменты отсеиваем заранее: показать нечего, а врач бы решил, что
  // орган «не нашёлся в 3D», хотя его просто нет в маске.
  const живые = [];
  for (const с of список) {
    const вокселей = подсчётВокселей(м.пиксели, с.индекс);
    if (вокселей) живые.push({ ...с, вокселей, мл: объёмМл(м.пиксели, с.индекс, м.spacing) });
  }
  if (!живые.length) {
    return { ок: false, причина: 'ни один из выбранных сегментов не размечен — сначала разметьте органы' };
  }

  const vp = _окно3D();
  if (!vp) return { ок: false, причина: 'Нет объёмного окна: выберите раскладку с 3D (например, MPR+3D)' };

  const re = getRenderingEngine();
  try {
    if (!_прежнее) {
      // [12.0.54] ИМЯ ТОМА — referencedId, а не uid. У Cornerstone 5.6.3 uid
      // актёра — случайная строка (BaseVolumeViewport: actorUID || uuidv4()),
      // и «Вернуть тело» просило движок поставить том с таким именем: на
      // стенде — «imageVolume with id: 273dccc1-… does not exist», тело не
      // возвращалось НИКОГДА. uid — только запасной путь для старых сборок.
      const а0 = vp.getActors?.()?.[0] || vp.getDefaultActor?.();
      const был = vp.getVolumeId?.() || а0?.referencedId || а0?.uid || null;
      _прежнее = { vpId: vp.id, volumeId: был };
    }
    await _cs.setVolumesForViewports(re, [{ volumeId: м.volumeId }], [vp.id], true);
    const актёр = vp.getDefaultActor?.()?.actor;
    const свойство = актёр?.getProperty?.();
    if (свойство) {
      const ctf = свойство.getRGBTransferFunction?.(0);
      if (ctf?.removeAllPoints) {
        ctf.removeAllPoints();
        ctf.addRGBPoint(0, 0, 0, 0);
        for (const с of живые) {
          const [r, g, b] = цветВRgb(с.цвет || '#ef4444');
          ctf.addRGBPoint(с.индекс, r, g, b);
        }
      }
      const ofun = свойство.getScalarOpacity?.(0);
      if (ofun?.removeAllPoints) {
        // Ступенька вокруг каждого номера; всё между номерами — прозрачно.
        // Полуширина 0,45 взята не с потолка: номера сегментов целые, поэтому
        // 0,45 не задевает соседний номер даже после линейной интерполяции
        // между контрольными точками, а 0,5 задела бы ровно на границе.
        ofun.removeAllPoints();
        ofun.addPoint(0, 0);
        for (const с of живые) {
          ofun.addPoint(с.индекс - 0.5, 0);
          ofun.addPoint(с.индекс - 0.45, 0.92);
          ofun.addPoint(с.индекс + 0.45, 0.92);
          ofun.addPoint(с.индекс + 0.5, 0);
        }
      }
      свойство.setShade?.(true);
      свойство.setAmbient?.(0.3);
      свойство.setDiffuse?.(0.75);
      свойство.setSpecular?.(0.1);
      свойство.setScalarOpacityUnitDistance?.(0, 1.0);
      свойство.setInterpolationTypeToLinear?.();
    }
    const текстура = _освежитьТекстуру(м.volumeId);
    vp.render?.();
    log.info(`[Органы-3D] окно ${vp.id}, текстура маски: ${текстура}`);
    const мл = живые.reduce((с, о) => с + о.мл, 0);
    return {
      ок: true,
      мл,
      окно: vp.id,
      вокселей: живые.reduce((с, о) => с + о.вокселей, 0),
      органы: живые.map((о) => ({ индекс: о.индекс, цвет: о.цвет, мл: о.мл, вокселей: о.вокселей })),
    };
  } catch (e) {
    log.warn('[Орган-3D] изоляция:', e.message);
    return { ок: false, причина: 'Не удалось подменить том окна: ' + e.message };
  }
}

/**
 * Показать один орган. [12.0.18] Теперь это частный случай изолироватьОрганы —
 * поведение то же, чтобы прежние вызывающие (featureBindings, clinicalTools)
 * не заметили разницы.
 */
export async function изолироватьОрган({ индекс = 1, цвет = '#ef4444' } = {}) {
  // Порядок проверок тот же, что был до 12.0.18: сперва доступность маски,
  // потом пустота сегмента — иначе «нет холста» превратилось бы в «сегмент пуст».
  const м = томМаски();
  if (м.ошибка) return { ок: false, причина: м.ошибка };
  if (!подсчётВокселей(м.пиксели, индекс)) {
    return { ок: false, причина: `Сегмент ${индекс} пуст — сначала закрасьте орган кистью этим сегментом` };
  }
  const р = await изолироватьОрганы({ сегменты: [{ индекс, цвет }] });
  if (!р.ок) return р;
  return { ок: true, мл: р.мл, вокселей: р.вокселей };
}

/** Вернуть в окно прежний том. Пресет пере-применяет вызывающий. */
export async function вернутьТело() {
  if (!_прежнее?.volumeId) return { ок: false, причина: 'Возвращать нечего: тело и не пряталось' };
  try {
    const re = getRenderingEngine();
    const vp = re?.getViewport(_прежнее.vpId);
    if (!vp) return { ок: false, причина: 'Окно, где прятали тело, уже закрыто' };
    await _cs.setVolumesForViewports(re, [{ volumeId: _прежнее.volumeId }], [vp.id], true);
    vp.render?.();
    _прежнее = null;
    return { ок: true };
  } catch (e) {
    return { ок: false, причина: e.message };
  }
}

export function телоСпрятано() { return !!_прежнее; }

/**
 * STL органа: бинаризованная маска → marching cubes по ISO 0.5 (процессорный
 * путь vtkModule — он принимает шаг вокселя и отдаёт МИЛЛИМЕТРЫ) → файл.
 */
export async function stlОргана({ индекс = 1 } = {}) {
  const м = томМаски();
  if (м.ошибка) return { ок: false, причина: м.ошибка };
  // [11.1.41] ПОЧЕМУ «ПОВЕРХНОСТЬ НЕ ПОСТРОИЛАСЬ» (жалоба владельца).
  // Сетка строилась по ВСЕМУ тому: 512×512×818 = 214 млн вокселей, а
  // marching cubes требует под них ещё Int16-копию на 428 МБ. Браузер падал
  // по памяти, VTK возвращал пустой объект, и мы говорили «не построилась»
  // вместо «не хватило памяти». Орган занимает малую часть тома — считаем
  // по вырезке вокруг него и смещаем сетку обратно в координаты пациента.
  const вырезка = вырезатьПоРамке(м.пиксели, м.dims, индекс, 2);
  if (!вырезка) return { ок: false, причина: `Сегмент ${индекс} пуст — STL строить не из чего` };
  try {
    const { runMarchingCubes, exportSTL } = await import('../compute/vtkModule.js');
    const sp = м.spacing || [1, 1, 1];
    const origin = [вырезка.смещение[0] * sp[0], вырезка.смещение[1] * sp[1], вырезка.смещение[2] * sp[2]];
    log.info(`[STL] вырезка ${вырезка.dims.join('×')} вокселей вместо ${м.dims.join('×')} — по всему тому не хватало памяти`);
    const mesh = await runMarchingCubes(вырезка.бин, вырезка.dims, sp, 0.5, origin);
    if (!mesh?.points?.length) {
      return { ок: false, причина: 'Поверхность не построилась даже по вырезке: сообщите об этом — нужен размер вырезки из журнала ([STL] вырезка …)' };
    }
    const имя = `organ_seg${индекс}_${Date.now()}.stl`;
    const r = await exportSTL(mesh, имя);
    return r?.success
      ? { ок: true, имя, треугольников: Math.round((mesh.polys?.length || 0) / 4), sizeKb: r.sizeKb }
      : { ок: false, причина: 'Сетка построилась, а файл не записался' };
  } catch (e) {
    return { ок: false, причина: e.message };
  }
}

export default { подсчётВокселей, объёмМл, бинаризоватьСегмент, цветВRgb, томМаски, изолироватьОрган, вернутьТело, телоСпрятано, stlОргана, ступениПравок, забытьПравкиМаски };
